import {useMemo, useState} from 'react';
import {useDebounce} from 'react-use';

import {graphqlClient} from '@/services/graphqlClient';
import {
  type FindMedicinalProductsQuery,
  useCreateMedicationNameMutation,
  useDeleteMedicationNameMutation,
  useFindMedicinalProductsQuery,
  useGetMedicationNamesQuery,
} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';

export const CREATE_NEW_MEDICATION_VALUE = '__create__';

export type MedicationOptionType = 'create' | 'local' | 'product';

export type MedicationOption = {
  value: string;
  label: string;
  keywords: string[];
  type: MedicationOptionType;
  commonName?: string;
  productName?: string;
  productId?: string;
};

type MedicinalProductName = NonNullable<
  FindMedicinalProductsQuery['findMatchingMedicinalProductNames'][number]
>;

export const normalizeMedicationLabel = (value: string) =>
  value.trim().toLowerCase();

const isMedicinalProductName = (
  product: MedicinalProductName | null
): product is MedicinalProductName => product !== null;

const hasExactLabel = (options: MedicationOption[], value: string) => {
  const normalizedValue = normalizeMedicationLabel(value);

  return options.some(
    option => normalizeMedicationLabel(option.label) === normalizedValue
  );
};

const createLocalOption = (value: string, label: string): MedicationOption => ({
  value,
  label,
  keywords: [normalizeMedicationLabel(label)],
  type: 'local',
});

const createProductOption = (
  value: string,
  label: string,
  commonName?: string,
  productName = label
): MedicationOption => ({
  value,
  label,
  keywords: [
    normalizeMedicationLabel(label),
    commonName && normalizeMedicationLabel(commonName),
  ].filter(Boolean) as string[],
  type: 'product',
  commonName,
  productName,
  productId: value,
});

const createNewMedicationOption = (label: string): MedicationOption => ({
  value: CREATE_NEW_MEDICATION_VALUE,
  label,
  keywords: [normalizeMedicationLabel(label)],
  type: 'create',
});

const dedupeMedicationOptions = (options: MedicationOption[]) => {
  const seen = new Set<string>();

  return options.filter(option => {
    const key = `${option.type}:${option.value}`;
    if (seen.has(key)) return false;

    seen.add(key);
    return true;
  });
};

export function useMedicationInputOptions(
  inputValue: string,
  value: string,
  currentProductId?: string
) {
  const normalizedInputValue = inputValue.trim();
  const [createdOptions, setCreatedOptions] = useState<MedicationOption[]>([]);
  const {debouncedValue: debouncedInputValue, cancelDebounce} =
    useDebouncedMedicationSearch(normalizedInputValue);

  const {data: medicationNamesData, isFetching: isFetchingMedicationNames} =
    useGetMedicationNamesQuery(graphqlClient, undefined, {});

  const {data: medicinalProductsData, isFetching: isFetchingMedicinalProducts} =
    useFindMedicinalProductsQuery(
      graphqlClient,
      {name: debouncedInputValue},
      {
        enabled: debouncedInputValue.length > 2,
        staleTime: Infinity,
      }
    );

  const invalidateMedicationNames = () => {
    queryClient.invalidateQueries({
      queryKey: useGetMedicationNamesQuery.getKey(),
    });
  };

  const createMedicationName = useCreateMedicationNameMutation(graphqlClient, {
    onSuccess: data => {
      const medication = data.createMedicationName;

      setCreatedOptions(prevOptions =>
        prevOptions.some(option => option.value === medication.id)
          ? prevOptions
          : [createLocalOption(medication.id, medication.name), ...prevOptions]
      );
      invalidateMedicationNames();
    },
  });

  const deleteMedicationName = useDeleteMedicationNameMutation(graphqlClient, {
    onSuccess: (_data, variables) => {
      setCreatedOptions(prevOptions =>
        prevOptions.filter(option => option.value !== variables.id)
      );
      invalidateMedicationNames();
    },
  });

  const localOptions = useMemo(() => {
    const fetchedOptions = (medicationNamesData?.getMedicationNames ?? []).map(
      medication => createLocalOption(medication.id, medication.name)
    );

    return dedupeMedicationOptions([...createdOptions, ...fetchedOptions]);
  }, [createdOptions, medicationNamesData?.getMedicationNames]);

  const productOptions = useMemo(
    () =>
      (medicinalProductsData?.findMatchingMedicinalProductNames ?? [])
        .filter(isMedicinalProductName)
        .sort((a, b) => {
          const nameCompare = a.productName.localeCompare(b.productName);
          if (nameCompare !== 0) return nameCompare;

          const strengthA = parseFloat(a.strength ?? '') || 0;
          const strengthB = parseFloat(b.strength ?? '') || 0;
          return strengthA - strengthB;
        })
        .map(product =>
          createProductOption(
            product.productId,
            `${product.productName}${
              product.strength ? ` (${product.strength})` : ''
            }`,
            product.commonName ?? undefined,
            product.productName
          )
        ),
    [medicinalProductsData?.findMatchingMedicinalProductNames]
  );

  const matchingLocalOptions = useMemo(
    () =>
      normalizedInputValue
        ? localOptions.filter(option =>
            option.label
              .toLowerCase()
              .includes(normalizedInputValue.toLowerCase())
          )
        : localOptions,
    [localOptions, normalizedInputValue]
  );

  const items = useMemo(() => {
    const currentProductOption =
      currentProductId && value
        ? [createProductOption(currentProductId, value)]
        : [];
    const baseOptions = dedupeMedicationOptions([
      ...matchingLocalOptions,
      ...currentProductOption,
      ...productOptions,
    ]);

    const createOption =
      normalizedInputValue && !hasExactLabel(baseOptions, inputValue)
        ? [createNewMedicationOption(normalizedInputValue)]
        : [];

    return [...createOption, ...baseOptions];
  }, [
    currentProductId,
    inputValue,
    matchingLocalOptions,
    normalizedInputValue,
    productOptions,
    value,
  ]);

  return {
    createMedicationName,
    deleteMedicationName,
    isLoading:
      isFetchingMedicationNames ||
      isFetchingMedicinalProducts ||
      createMedicationName.isPending ||
      deleteMedicationName.isPending,
    items,
    cancelDebounce,
  };
}

function useDebouncedMedicationSearch(value: string) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const [, cancelDebounce] = useDebounce(() => setDebouncedValue(value), 300, [
    value,
  ]);

  return {debouncedValue, cancelDebounce};
}
